"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ScoreService = void 0;
const ethers_1 = require("ethers");
const __1 = require("..");
class ScoreService {
    constructor(arcxLogger, offChainStorage, onChainStorage, ipfsService, signatureService, globalService, transactionService) {
        this.arcxLogger = arcxLogger;
        this.offChainStorage = offChainStorage;
        this.onChainStorage = onChainStorage;
        this.ipfsService = ipfsService;
        this.signatureService = signatureService;
        this.globalService = globalService;
        this.transactionService = transactionService;
    }
    fetchScores(params) {
        return __awaiter(this, void 0, void 0, function* () {
            let [globals, allManifests] = yield Promise.all([
                this.globalService.fetchGlobals(params.chainId),
                this.offChainStorage.readAllManifests(params.chainId),
            ]);
            if (params.protocols && params.protocols.length > 0) {
                allManifests = allManifests.filter((manifest) => { var _a; return (_a = params.protocols) === null || _a === void 0 ? void 0 : _a.includes(manifest.name); });
            }
            if (allManifests.length === 0) {
                this.arcxLogger.warning(`ScoreService::fetchScores => ${params.protocols} not found`);
                return [];
            }
            const fetchScorePromises = [];
            for (const manifest of allManifests) {
                // TODO: Handle custom limit-offset with multiple passed protocols
                if (manifest.is_in_root && !params.enforceOffchain) {
                    fetchScorePromises.push(this.onChainStorage.readScores(Object.assign(Object.assign({}, params), { protocol: manifest.name, epoch: params.epoch || globals.current_epoch })));
                }
                else {
                    fetchScorePromises.push(this.offChainStorage.readScores(Object.assign(Object.assign({}, params), { protocol: manifest.name })));
                }
            }
            const scores = (yield Promise.all(fetchScorePromises)).flat();
            if (!params.enforceOffchain && params.format === 'full') {
                const manifestMap = {};
                // Can fetch manifest by either its plain name or its bytes32 hash
                allManifests.forEach((manifest) => {
                    manifestMap[manifest.name] = manifest;
                    manifestMap[ethers_1.utils.formatBytes32String(manifest.name)] = manifest;
                });
                yield Promise.all(scores.map((score) => __awaiter(this, void 0, void 0, function* () {
                    score = yield this.populateExplanations(score, manifestMap[score.protocol], params.chainId);
                })));
            }
            return scores;
        });
    }
    fetchScoresCount(params) {
        return __awaiter(this, void 0, void 0, function* () {
            let [globals, allManifests] = yield Promise.all([
                this.globalService.fetchGlobals(params.chainId),
                this.offChainStorage.readAllManifests(params.chainId),
            ]);
            if (params.protocols && params.protocols.length > 0) {
                allManifests = allManifests.filter((manifest) => { var _a; return (_a = params.protocols) === null || _a === void 0 ? void 0 : _a.includes(manifest.name); });
                if (allManifests.length === 0) {
                    this.arcxLogger.warning(`ScoreService::fetchScoresCount => Manifests for ${params.protocols} not found`);
                    return 0;
                }
            }
            const itemCount = (yield Promise.all(allManifests.map((manifest) => __awaiter(this, void 0, void 0, function* () {
                if (manifest.is_in_root) {
                    return this.onChainStorage.readScoresCount(Object.assign(Object.assign({}, params), { protocol: manifest.name, epoch: globals.current_epoch }));
                }
                else {
                    return this.offChainStorage.readScoresCount(Object.assign(Object.assign({}, params), { protocol: manifest.name }));
                }
            })))).reduce((acc, count) => acc + count, 0);
            return itemCount;
        });
    }
    fetchScoreStatuses(chainId) {
        return __awaiter(this, void 0, void 0, function* () {
            const manifests = yield this.fetchAllManifests(chainId);
            const scoreStatuses = {};
            manifests.forEach((manifest) => {
                scoreStatuses[manifest.name] = {
                    ui: manifest.is_publicly_visible ? 'active' : 'hidden',
                    proof: manifest.is_in_root ? 'attached' : 'detached',
                };
            });
            return scoreStatuses;
        });
    }
    fetchManifest(chainId, protocol) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.offChainStorage.readManifest(chainId, protocol);
        });
    }
    fetchAllManifests(chainId) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.offChainStorage.readAllManifests(chainId);
        });
    }
    fetchApiScoreAttributes(chainId, protocols = []) {
        return __awaiter(this, void 0, void 0, function* () {
            const allManifests = yield this.offChainStorage.readAllManifests(chainId);
            // If nothing is provided, get everything
            if (protocols.length === 0) {
                protocols = allManifests.map((manifest) => manifest.name);
            }
            const apiScoreAttributes = [];
            for (const protocol of protocols) {
                const manifest = allManifests.find((manifest) => manifest.name === protocol);
                if (manifest) {
                    apiScoreAttributes.push({
                        name: manifest.name,
                        nameByte32: ethers_1.utils.formatBytes32String(manifest.name),
                        categories: manifest.categories,
                        defaults: manifest.defaults,
                        externalUri: manifest.external_uri || '',
                        title: manifest.title,
                        active: manifest.is_publicly_visible,
                        description: manifest.description,
                        sources: manifest.sources || [],
                        max: manifest.max_value || manifest.pop_max || '0',
                        min: manifest.min_value || manifest.pop_min || '0',
                        populations: manifest.populations || [],
                        ranges: manifest.ranges || [],
                    });
                }
                else {
                    this.arcxLogger.warning(`ScoreService::fetchApiScoreAttributes => ${protocol} not found`);
                }
            }
            return apiScoreAttributes;
        });
    }
    toggleScore(chainId, protocol) {
        return __awaiter(this, void 0, void 0, function* () {
            const manifest = yield this.fetchManifest(chainId, protocol);
            if (!manifest) {
                const message = `ScoreService::toggleScore => ${protocol} not found`;
                this.arcxLogger.critical(message);
                throw new Error(message);
            }
            yield this.publishManifest(chainId, protocol, {
                is_publicly_visible: !manifest.is_publicly_visible,
            });
            return !manifest.is_publicly_visible;
        });
    }
    toggleProof(chainId, protocol) {
        return __awaiter(this, void 0, void 0, function* () {
            const manifest = yield this.fetchManifest(chainId, protocol);
            if (!manifest) {
                const message = `ScoreService::toggleProof => ${protocol} not found`;
                this.arcxLogger.critical(message);
                throw new Error(message);
            }
            yield this.publishManifest(chainId, protocol, { is_in_root: !manifest.is_in_root });
            return !manifest.is_in_root;
        });
    }
    publishScoreStatistics(manifest, epoch) {
        return __awaiter(this, void 0, void 0, function* () {
            const newScoreStats = new __1.ScoreStatistics(epoch, manifest.name, manifest.pop_max || '0', manifest.pop_min || '0', manifest.populations || [], manifest.ranges || []);
            yield this.onChainStorage.createStats(newScoreStats);
        });
    }
    publishManifest(chainId, name, manifest) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.offChainStorage.updateManifest(chainId, name, manifest);
        });
    }
    publishOnChainScores(scores) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.onChainStorage.createScores(scores);
        });
    }
    publishIpfsData(newLeavesWithProofs, epoch, chainId) {
        return __awaiter(this, void 0, void 0, function* () {
            const currentSignatures = yield this.signatureService.fetchSignatures(chainId, epoch);
            yield this.ipfsService.publishIpfs(newLeavesWithProofs, currentSignatures, epoch);
        });
    }
    removeDataBefore(epoch) {
        return __awaiter(this, void 0, void 0, function* () {
            yield Promise.all([
                this.onChainStorage.deleteScoresBefore(epoch),
                this.onChainStorage.deleteStatisticsBefore(epoch),
                this.signatureService.removeSignaturesBefore(epoch),
            ]);
        });
    }
    removeDataAt(epoch) {
        return __awaiter(this, void 0, void 0, function* () {
            yield Promise.all([
                this.onChainStorage.deleteScoresAt(epoch),
                this.onChainStorage.deleteStatisticsAt(epoch),
            ]);
        });
    }
    fetchResultingLimit(format, providedLimit) {
        let defaultLimit = 0;
        switch (format) {
            case 'simple':
                defaultLimit = this.offChainStorage.FORMAT_SIMPLE_LIMIT;
                break;
            case 'proof':
                defaultLimit = this.offChainStorage.FORMAT_PROOF_LIMIT;
                break;
            case 'full':
                defaultLimit = this.offChainStorage.FORMAT_FULL_LIMIT;
                break;
        }
        return providedLimit && providedLimit < defaultLimit ? providedLimit : defaultLimit;
    }
    populateExplanations(score, manifest, chainId) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            score.metadata = {
                rank: ((_a = score === null || score === void 0 ? void 0 : score.metadata) === null || _a === void 0 ? void 0 : _a.rank) || 0,
                percentile: ((_b = score === null || score === void 0 ? void 0 : score.metadata) === null || _b === void 0 ? void 0 : _b.percentile) || 0,
                explanations: ((_c = score.metadata) === null || _c === void 0 ? void 0 : _c.explanations) || [],
            };
            const missingExplanations = this.fetchMissingExplanations(score, manifest);
            const pendingTransactionsExplanations = yield this.transactionService.fetchAndParsePendingExplanations(chainId, score.account, manifest.name, score.metadata.explanations);
            score.metadata.explanations = [
                ...score.metadata.explanations,
                ...missingExplanations,
                ...pendingTransactionsExplanations,
            ];
            return score;
        });
    }
    fetchMissingExplanations(score, manifest) {
        var _a;
        const missingExplanations = [];
        if (!score ||
            !score.metadata ||
            !score.metadata.explanations ||
            score.metadata.explanations.length === 0) {
            return manifest.defaults;
        }
        const explanations = score.metadata.explanations;
        for (const defaultExplanation of manifest.defaults) {
            const isWildcard = defaultExplanation.id && defaultExplanation.id.includes('.*');
            if (isWildcard) {
                const wildcardId = ((_a = defaultExplanation.id) === null || _a === void 0 ? void 0 : _a.replace('.*', '')) || '';
                const hasWildcard = explanations.find((scoreExplanation) => {
                    return scoreExplanation.id && scoreExplanation.id.includes(wildcardId);
                });
                if (!hasWildcard)
                    missingExplanations.push(defaultExplanation);
            }
            else {
                const hasExplanation = explanations.find((scoreExplanation) => {
                    return scoreExplanation.id && defaultExplanation.id === scoreExplanation.id;
                });
                if (!hasExplanation)
                    missingExplanations.push(defaultExplanation);
            }
        }
        return missingExplanations;
    }
}
exports.ScoreService = ScoreService;
//# sourceMappingURL=ScoreService.js.map